import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fmtStamp } from '@/utils/dateFmt';
import { toast } from 'sonner';
import { EnvelopeSimple, Eye, EyeSlash, Warning, ArrowClockwise, MagnifyingGlass, CaretLeft, CaretRight, CheckCircle, XCircle, Clock } from '@phosphor-icons/react';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useNavigate } from 'react-router-dom';
import { DateField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  entregue: { label: 'Entregue', cls: 'bg-[#bbd6d5]/40 text-[#3f5a4e] border-[#9ec3bf]' },
  enviado: { label: 'Enviado', cls: 'bg-[#d7bd7b]/25 text-[#8a6d2e] border-[#d7bd7b]' },
  bounce_soft: { label: 'Bounce suave', cls: 'bg-red-50 text-red-600 border-red-200' },
  bounce_hard: { label: 'Bounce', cls: 'bg-red-100 text-red-700 border-red-300' },
  falhado: { label: 'Falhado', cls: 'bg-red-100 text-red-700 border-red-300' },
};

const fmtDateTime = (iso) => (iso ? fmtStamp(iso) : '-');

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || 'Desconhecido', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  return <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full border ${m.cls}`} data-testid={`status-badge-${status}`}>{m.label}</span>;
}

function BenchmarkCard({ title, value, unit, inRange, benchmarkText }) {
  return (
    <div className="bg-white border border-[#e5e7e3] rounded-2xl p-4" data-testid={`benchmark-${title.toLowerCase().replace(/ /g, '-')}`}>
      <p className="text-[11px] text-[#7A8276] uppercase tracking-wide">{title}</p>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="font-heading text-2xl text-[#2D332B]">{value}</span>
        <span className="text-sm text-[#7A8276]">{unit}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className={`w-2 h-2 rounded-full ${inRange ? 'bg-[#657161]' : 'bg-red-500'}`} />
        <span className={`text-[11px] ${inRange ? 'text-[#657161]' : 'text-red-600'}`}>{inRange ? 'Dentro do esperado' : 'Fora do esperado'}</span>
      </div>
      <p className="text-[10px] text-[#A8AFA3] mt-1">Referência: {benchmarkText}</p>
    </div>
  );
}

function EmailsTab() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ logs: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ email_type: '', status: '', opened: '', search: '', date_from: '', date_to: '' });
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '25', ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/email-logs?${params.toString()}`),
        axios.get(`${API}/api/admin/email-logs/stats?${filters.date_from ? `date_from=${filters.date_from}&` : ''}${filters.date_to ? `date_to=${filters.date_to}` : ''}`),
      ]);
      setData(logsRes.data); setStats(statsRes.data);
    } catch { toast.error('Erro ao carregar emails'); } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };

  return (
    <div className="space-y-5" data-testid="emails-tab">
      {/* Benchmarks */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BenchmarkCard title="Taxa de entrega" value={stats.delivery_rate} unit="%" inRange={stats.in_range?.delivery_rate} benchmarkText="acima de 98%" />
          <BenchmarkCard title="Taxa de abertura" value={stats.open_rate} unit="%" inRange={stats.in_range?.open_rate} benchmarkText="40% a 60%" />
          <BenchmarkCard title="Taxa de bounce" value={stats.bounce_rate} unit="%" inRange={stats.in_range?.bounce_rate} benchmarkText="abaixo de 2%" />
        </div>
      )}

      {/* Alertas */}
      {stats?.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="email-alerts">
          {stats.alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-[13px] text-red-700">
              <Warning size={16} weight="fill" className="flex-shrink-0" /> {a.text}
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-[#e5e7e3] rounded-2xl p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <div className="col-span-2 sm:col-span-1 relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-[#A8AFA3]" />
          <input value={filters.search} onChange={e => setF('search', e.target.value)} placeholder="Nome ou email" className="w-full pl-8 pr-2 py-1.5 border border-[#e5e7e3] rounded-lg text-sm" data-testid="filter-search" />
        </div>
        <select value={filters.email_type} onChange={e => setF('email_type', e.target.value)} className="border border-[#e5e7e3] rounded-lg text-sm px-2 py-1.5 bg-white" data-testid="filter-type">
          <option value="">Todos os tipos</option>
          {(stats?.email_types || []).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <select value={filters.status} onChange={e => setF('status', e.target.value)} className="border border-[#e5e7e3] rounded-lg text-sm px-2 py-1.5 bg-white" data-testid="filter-status">
          <option value="">Todos os estados</option>
          <option value="entregue">Entregue</option>
          <option value="enviado">Enviado</option>
          <option value="bounce_soft">Bounce suave</option>
          <option value="bounce_hard">Bounce</option>
          <option value="falhado">Falhado</option>
        </select>
        <select value={filters.opened} onChange={e => setF('opened', e.target.value)} className="border border-[#e5e7e3] rounded-lg text-sm px-2 py-1.5 bg-white" data-testid="filter-opened">
          <option value="">Aberto: todos</option>
          <option value="yes">Aberto</option>
          <option value="no">Não aberto</option>
        </select>
        <DateField value={filters.date_from} onChange={e => setF('date_from', e.target.value)} className="border border-[#e5e7e3] rounded-lg text-sm px-2 py-1.5" data-testid="filter-date-from" />
        <DateField value={filters.date_to} onChange={e => setF('date_to', e.target.value)} className="border border-[#e5e7e3] rounded-lg text-sm px-2 py-1.5" data-testid="filter-date-to" />
      </div>

      {/* Tabela */}
      <div className="bg-white border border-[#e5e7e3] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="emails-table">
            <thead className="bg-[#f7f8f6] text-[#7A8276] text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Destinatário</th>
                <th className="text-left px-3 py-2">Tipo</th>
                <th className="text-left px-3 py-2">Assunto</th>
                <th className="text-left px-3 py-2">Data</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-center px-3 py-2">Aberto</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#A8AFA3]">A carregar...</td></tr>
              ) : data.logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-[#A8AFA3]" data-testid="emails-empty">Sem emails registados.</td></tr>
              ) : data.logs.map(log => (
                <tr key={log.id} className="border-t border-[#f0f0ec] hover:bg-[#faf8f4]" data-testid={`email-row-${log.id}`}>
                  <td className="px-3 py-2">
                    <div className="text-[#2D332B]">{log.patient_name || log.to_name || '-'}</div>
                    <div className="text-[11px] text-[#A8AFA3]">{log.to_email}</div>
                  </td>
                  <td className="px-3 py-2 text-[#657161] text-[12px]">{log.email_type_label}</td>
                  <td className="px-3 py-2 text-[#2D332B] text-[12px] max-w-[220px] truncate" title={log.subject}>{log.subject}</td>
                  <td className="px-3 py-2 text-[#7A8276] text-[12px] whitespace-nowrap">{fmtDateTime(log.sent_at)}</td>
                  <td className="px-3 py-2"><StatusBadge status={log.delivery_status} /></td>
                  <td className="px-3 py-2 text-center">
                    {log.opened ? <Eye size={16} weight="fill" className="text-[#657161] inline" title={fmtDateTime(log.opened_at)} data-testid={`opened-yes-${log.id}`} /> : <EyeSlash size={16} className="text-[#c9cdc4] inline" data-testid={`opened-no-${log.id}`} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Paginação */}
        {data.pages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-[#f0f0ec] text-[12px] text-[#7A8276]">
            <span>{data.total} emails</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1 disabled:opacity-30" data-testid="page-prev"><CaretLeft size={16} /></button>
              <span>{page} / {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="p-1 disabled:opacity-30" data-testid="page-next"><CaretRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const BLOCKER_LABELS = { convite_bounce: 'Convite com bounce', nao_aberto_72h: 'Convite não aberto (72h)', sem_login_72h: 'Sem primeiro login (72h)' };

function OnboardingTab() {
  const [data, setData] = useState({ patients: [], total: 0, blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/admin/onboarding-funnel`); setData(data); }
    catch { toast.error('Erro ao carregar onboarding'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resend = async (pid, e) => {
    e.stopPropagation();
    try { await axios.post(`${API}/api/admin/onboarding-funnel/${pid}/resend-invite`); toast.success('Convite reenviado'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro ao reenviar'); }
  };

  const Step = ({ done, label }) => (
    <div className="flex items-center gap-1">
      {done ? <CheckCircle size={15} weight="fill" className="text-[#657161]" /> : <XCircle size={15} className="text-[#d0d3ca]" />}
      <span className={`text-[11px] ${done ? 'text-[#2D332B]' : 'text-[#A8AFA3]'}`}>{label}</span>
    </div>
  );

  const rows = onlyBlocked ? data.patients.filter(p => p.blocked) : data.patients;

  return (
    <div className="space-y-4" data-testid="onboarding-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <div className="bg-white border border-[#e5e7e3] rounded-xl px-3 py-1.5 text-[12px]"><span className="text-[#7A8276]">Pacientes:</span> <strong>{data.total}</strong></div>
          <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-1.5 text-[12px] text-red-700"><span>Bloqueadas:</span> <strong>{data.blocked}</strong></div>
        </div>
        <label className="flex items-center gap-1.5 text-[12px] text-[#657161] cursor-pointer">
          <input type="checkbox" checked={onlyBlocked} onChange={e => setOnlyBlocked(e.target.checked)} data-testid="only-blocked-toggle" /> Mostrar só bloqueadas
        </label>
      </div>

      <div className="bg-white border border-[#e5e7e3] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="onboarding-table">
            <thead className="bg-[#f7f8f6] text-[#7A8276] text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Paciente</th>
                <th className="text-left px-3 py-2">Funil</th>
                <th className="text-left px-3 py-2">Bloqueios</th>
                <th className="text-right px-3 py-2">Ação</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-8 text-[#A8AFA3]">A carregar...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-[#A8AFA3]" data-testid="onboarding-empty">Sem pacientes.</td></tr>
              ) : rows.map(p => (
                <tr key={p.patient_id} className="border-t border-[#f0f0ec] hover:bg-[#faf8f4] cursor-pointer" onClick={() => navigate(`/admin/pacientes/${p.patient_id}`)} data-testid={`onboarding-row-${p.patient_id}`}>
                  <td className="px-3 py-2">
                    <div className="text-[#2D332B]">{p.patient_name || '-'}</div>
                    <div className="text-[11px] text-[#A8AFA3]">{p.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      <Step done={p.invited} label="Convite" />
                      <Step done={p.invite_opened} label="Aberto" />
                      <Step done={p.registered} label="Registo" />
                      <Step done={p.logged_in} label="1º login" />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {p.blockers.length === 0 ? <span className="text-[11px] text-[#A8AFA3]">-</span> : p.blockers.map(b => (
                      <span key={b} className="inline-block text-[10px] bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5 mr-1 mb-1">{BLOCKER_LABELS[b] || b}</span>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {p.blocked && (
                      <button onClick={(e) => resend(p.patient_id, e)} className="inline-flex items-center gap-1 text-[11px] border border-[#657161] text-[#657161] rounded-full px-2.5 py-1 hover:bg-[#657161]/10" data-testid={`resend-invite-${p.patient_id}`}>
                        <ArrowClockwise size={12} /> Reenviar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function EmailsPage({ forceTab }) {
  useRequireAdmin();
  const [tab, setTab] = useState(forceTab || 'emails');
  return (
    <div className="fade-in max-w-6xl mx-auto" data-testid="emails-page">
      {!forceTab && <div className="flex items-center gap-2 mb-4">
        <EnvelopeSimple size={24} weight="duotone" style={{ color: '#657161' }} />
        <h1 className="font-heading font-medium text-2xl text-[#2D332B]">Emails</h1>
      </div>}
      {!forceTab && <div className="flex gap-2 mb-5">
        <button onClick={() => setTab('emails')} className={`px-4 py-1.5 rounded-full text-sm font-medium ${tab === 'emails' ? 'bg-[#657161] text-white' : 'bg-white border border-[#e5e7e3] text-[#7A8276]'}`} data-testid="tab-emails">Registo de emails</button>
        <button onClick={() => setTab('onboarding')} className={`px-4 py-1.5 rounded-full text-sm font-medium ${tab === 'onboarding' ? 'bg-[#657161] text-white' : 'bg-white border border-[#e5e7e3] text-[#7A8276]'}`} data-testid="tab-onboarding">Onboarding</button>
      </div>}
      {tab === 'emails' ? <EmailsTab /> : <OnboardingTab />}
    </div>
  );
}
